import { ComponentTypes } from '@wix/editor-const';
import { ComponentType } from '@wix/ambassador-devcenter-components-v1-component/types';
import type { CompRef } from '@wix/document-services-types';
import type { EditorPointer } from '@wix/editor-document-logic-api';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { ApplicationDataPlatformService } from './ApplicationDataPlatformService';

export type CustomCssUnsupportedReason =
  | 'unsupported-component-type'
  | 'legacy-style-schema';

export interface CustomCssCapability {
  supported: boolean;
  targetRef?: CompRef;
  reason?: CustomCssUnsupportedReason;
}

export interface CapabilityResults {
  customCss: CustomCssCapability;
}

export type ComponentCapability = keyof CapabilityResults;

export class CapabilitiesPlatformService extends AbstractComponentsPlatformService {
  readonly #applicationDataService: ApplicationDataPlatformService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.#applicationDataService = new ApplicationDataPlatformService(...args);
  }

  check<C extends ComponentCapability>(
    editorPointer: EditorPointer<CompRef>,
    capability: C,
  ): CapabilityResults[C] {
    return this.#getCapabilities(editorPointer)[capability];
  }

  #getCapabilities(editorPointer: EditorPointer<CompRef>): CapabilityResults {
    return {
      customCss: this.#getCustomCssCapability(editorPointer),
    };
  }

  #getCustomCssCapability(
    editorPointer: EditorPointer<CompRef>,
  ): CustomCssCapability {
    const componentType = this.virtualElementProvider
      .buildVirtualElement({ editorPointers: [editorPointer] })
      .getComponentType();

    if (this.dm.tpa.isTpaByCompType(componentType)) {
      return { supported: false, reason: 'legacy-style-schema' };
    }

    if (componentType === ComponentTypes.CustomElement) {
      return { supported: false, reason: 'unsupported-component-type' };
    }

    const appComponentData =
      this.#applicationDataService.getAppComponentDataByComponentType(
        componentType,
      );

    if (appComponentData?.type === ComponentType.WIDGET_OUT_OF_IFRAME) {
      return { supported: false, reason: 'legacy-style-schema' };
    }

    if (appComponentData?.type !== ComponentType.EDITOR_REACT_COMPONENT) {
      return { supported: false, reason: 'unsupported-component-type' };
    }

    return {
      supported: true,
      targetRef: this.editorPointerAPI.getDocumentPointer(editorPointer),
    };
  }
}
